import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: The Refiner\npageTitle: The Refiner | Covalent Network | Covalent\n---\n\n{% breadcrumb title=\"Refiner\" /%}\n\n# {% $markdoc.frontmatter.title %}\n\nThe Refiner is a concurrent Block Specimen data processing framework designed for scalable and verified data transformations. The output, one of many possible, is a Block Result; a one-to-one representation of block data returned from an RPC call to a blockchain node with optional and additional informative fields. And importantly, the Refiner can perform arbitrary transformations over any input Block Specimens concurrently with other transformations, which essentially means that the Refiner can concurrently re-execute ethereum blocks and transactions completely external to their source blockchain node client software, something that no blockchain node clients can currently do. By leveraging the Refiner's concurrent processing capability, one can effortlessly perform customizable data indexing with minimal overhead.  \n\nThe Block Results consequently indexed will power products such as [Covalent's Unified API](https://www.covalenthq.com/docs/unified-api/), and is the first of many steps needed to build our vision of a truly decentralized data infrastructure aimed at solving Ethereum's long term data availability and read scalability problem.\n\n{% diagram slug=\"block-refiner-process\" whitespace=\"true\" gradient=\"true\" /%}\n\n## Enrichment\n\nWith the Refiner live, Block Specimens can be enriched and transformed into Block Results by multiple operators on the Covalent Network. These verified Block Results can then be retrieved and indexed by anyone utilizing the Covalent Network for decentralized data access without the overhead of running full blockchain client nodes.\n\nThe net result is that anyone will be able to pull historical and live blocks that are complete with the data one could retrieve by calling the JSON-RPC layer but without the bottlenecks associated with the latter method. Furthermore, anyone wishing to analyze this data or build a web3 data application on top of the Covalent Network, such as block explorers, can pull this type of granular data without running a node.\n\nAn Ethereum transaction tracing engine is also on its way to unlock deeper insights into an Ethereum transaction. This work is iterative and research-oriented in nature (unlike Block Specimen to Block Result transformation, which is stable). The concurrent nature of Refiner's Block Specimen execution means that the network can quickly run **all** Ethereum blocks through the tracing engine.\n\n### Hardware Requirements\n\nThe recommended configurations will make sure the sync is not lag prone and the node is able to keep up with the Ethereum network.\n\n**Minimum**\n\n* 2 vCPUs (cores)\n* 3GB RAM\n* 200GB HDD free storage (mostly storing artifacts on local ipfs; can be pruned periodically)\n* 8 MBit/sec download Internet service\n\n**Recommended**\n\n* 4 vCPUs\n* 8 GB+ RAM\n* SSD with >= 500GB storage space\n* 25+ MBit/sec download Internet service\n\n**Software Requirements (Docker Setup)**\n\n* 64-bit Linux, Mac OS 13+\n* SSL certificates\n* docker, docker-compose, direnv\n\n## Resources\n\n* [Build Specifications - Github Repo](https://github.com/covalenthq/rudder/)\n\n* [Refiner Whitepaper](https://www.covalenthq.com/docs/covalent-network/refiner-whitepaper/)\n\n* [Refiner Onboarding](https://www.covalenthq.com/docs/covalent-network/operator-onboarding-refiner/)\n";
const filepath = "/docs/covalent-network/refiner.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
